/**
 * Platform Selector Component
 * Allows multi-selection of ad platforms (Meta, Google Display, Google Search, YouTube)
 */
import React from 'react';
import { Globe, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

// Platform configurations
export const PLATAFORMAS = [
  {
    id: 'meta_ads',
    nombre: 'Meta Ads',
    descripcion: 'Facebook / Instagram',
    icon: '📱',
    color: 'from-blue-500 to-purple-500',
    formatos: [
      { id: 'feed', nombre: 'Feed', aspect_ratio: '4:5', tipo: 'imagen', descripcion: 'Posts de feed' },
      { id: 'story', nombre: 'Story/Reel', aspect_ratio: '9:16', tipo: 'imagen', descripcion: 'Stories y Reels' }
    ],
    copyLimits: {
      headline: 40,
      descripcion: 125,
      texto_principal: 500
    },
    creditosBase: 23
  },
  {
    id: 'google_display',
    nombre: 'Google Display',
    descripcion: 'Performance Max / Display Network',
    icon: '🖼️',
    color: 'from-green-500 to-teal-500',
    formatos: [
      { id: 'horizontal', nombre: 'Horizontal', aspect_ratio: '1.91:1', tipo: 'imagen', descripcion: 'Banner 1200x628' },
      { id: 'cuadrado', nombre: 'Cuadrado', aspect_ratio: '1:1', tipo: 'imagen', descripcion: 'Cuadrado 1200x1200' },
      { id: 'logo', nombre: 'Logo', aspect_ratio: '1:1', tipo: 'logo', descripcion: 'Logo cuadrado (si existe)' }
    ],
    copyLimits: {
      headline: 30,
      descripcion: 90
    },
    creditosBase: 23
  },
  {
    id: 'google_search',
    nombre: 'Google Search',
    descripcion: 'Anuncios de texto',
    icon: '🔍',
    color: 'from-yellow-500 to-orange-500',
    formatos: [
      { id: 'text_ad', nombre: 'Anuncio de Texto', aspect_ratio: null, tipo: 'texto', descripcion: 'Solo texto' }
    ],
    copyLimits: {
      titulares: { max: 15, caracteres: 30 },
      descripciones: { max: 4, caracteres: 90 }
    },
    creditosBase: 5,
    noImage: true
  },
  {
    id: 'youtube',
    nombre: 'YouTube',
    descripcion: 'Video Ads',
    icon: '▶️',
    color: 'from-red-500 to-pink-500',
    formatos: [
      { id: 'video_16_9', nombre: 'Video 16:9', aspect_ratio: '16:9', tipo: 'video', descripcion: 'Video horizontal' }
    ],
    copyLimits: {
      headline: 100,
      descripcion: 500
    },
    creditosBase: 45
  }
];

// Helper functions
export const getPlatformById = (id) => PLATAFORMAS.find(p => p.id === id);

export const calcularCreditosPlataformas = (selectedPlatforms, brief) => {
  let total = 0;
  selectedPlatforms.forEach(platformId => {
    const platform = getPlatformById(platformId);
    if (!platform) return;
    
    platform.formatos.forEach(formato => {
      if (formato.tipo === 'logo' && !brief?.project_logo && !brief?.logo_url) {
        return;
      }
      if (formato.tipo === 'imagen') {
        total += 23;
      } else if (formato.tipo === 'video') {
        total += 45;
      } else if (formato.tipo === 'texto') {
        total += 5;
      }
    });
  });
  return total;
};

export const PlatformSelector = ({ 
  selectedPlatforms, 
  setSelectedPlatforms, 
  brief 
}) => {
  const handlePlatformToggle = (platformId) => {
    const isSelected = selectedPlatforms.includes(platformId);
    
    if (isSelected && selectedPlatforms.length === 1) {
      toast.info('Debes tener al menos una plataforma seleccionada');
      return;
    }
    
    setSelectedPlatforms(prev => 
      isSelected 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  return (
    <div className="bg-surface rounded-xl border border-border-subtle p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-text-main flex items-center gap-2">
            <Globe className="w-4 h-4 text-brand-500" />
            Plataformas de destino
          </h3>
          <p className="text-xs text-text-muted mt-1">
            Selecciona dónde publicarás estos anuncios
          </p>
        </div>
        <div className="text-right">
          <span className="text-xs text-text-muted">Coste total estimado:</span>
          <span className="ml-2 text-sm font-medium text-brand-500">
            {calcularCreditosPlataformas(selectedPlatforms, brief)} cr
          </span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {PLATAFORMAS.map(platform => {
          const isSelected = selectedPlatforms.includes(platform.id);
          return (
            <button
              key={platform.id}
              data-testid={`platform-card-${platform.id}`}
              onClick={() => handlePlatformToggle(platform.id)}
              className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                isSelected 
                  ? `border-brand/50 bg-gradient-to-br ${platform.color} bg-opacity-10` 
                  : 'border-border-subtle hover:border-border-subtle/80 bg-app-bg'
              }`}
            >
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-500" />
                </div>
              )}
              <span className="text-2xl mb-2 block">{platform.icon}</span>
              <h4 className="font-medium text-sm text-text-main">{platform.nombre}</h4>
              <p className="text-xs text-text-muted mt-1">{platform.descripcion}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {platform.formatos.map(f => (
                  <span 
                    key={f.id} 
                    className="text-[10px] px-1.5 py-0.5 bg-surface rounded border border-border-subtle text-text-muted"
                  >
                    {f.nombre}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Selected platforms summary */}
      {selectedPlatforms.length > 0 && (
        <div className="mt-4 p-3 bg-app-bg rounded-lg border border-border-subtle">
          <div className="text-xs text-text-muted mb-2">Formatos que se generarán:</div>
          <div className="flex flex-wrap gap-2">
            {selectedPlatforms.map(platformId => {
              const platform = getPlatformById(platformId);
              return platform?.formatos.map(formato => {
                if (formato.tipo === 'logo' && !brief?.project_logo && !brief?.logo_url) {
                  return null;
                }
                return (
                  <span 
                    key={`${platformId}-${formato.id}`}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r ${platform.color} text-white`}
                  >
                    <span>{platform.icon}</span>
                    {formato.nombre}
                    {formato.aspect_ratio && <span className="opacity-70">({formato.aspect_ratio})</span>}
                  </span>
                );
              });
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSelector;
